import React from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Bell, 
  AlertCircle, 
  ShoppingBag, 
  Users, 
  Package, 
  Calendar,
  CreditCard,
  Settings
} from "lucide-react";

interface NotificationTabsProps {
  activeTab: string;
  onTabChange: (value: string) => void;
  unreadCount: number;
  children: React.ReactNode;
}

export const NotificationTabs: React.FC<NotificationTabsProps> = ({
  activeTab,
  onTabChange,
  unreadCount,
  children
}) => {
  const { t } = useTranslation();

  return (
    <Tabs value={activeTab} onValueChange={onTabChange} className="w-full">
      <TabsList className="grid w-full grid-cols-8">
        <TabsTrigger value="all" className="flex items-center space-x-2">
          <Bell className="h-4 w-4" />
          <span className="hidden sm:inline">{t("notifications.tabs.all")}</span>
        </TabsTrigger>
        <TabsTrigger value="unread" className="flex items-center space-x-2">
          <AlertCircle className="h-4 w-4" />
          <span className="hidden sm:inline">{t("notifications.tabs.unread")}</span>
          {unreadCount > 0 && (
            <Badge variant="secondary" className="h-4 w-6 items-center justify-center rounded-full p-1 text-xs">
              {unreadCount > 99 ? "99+" : unreadCount}
            </Badge>
          )}
        </TabsTrigger>
        <TabsTrigger value="orders" className="flex items-center space-x-2">
          <ShoppingBag className="h-4 w-4" />
          <span className="hidden sm:inline">{t("notifications.tabs.orders")}</span>
        </TabsTrigger>
        <TabsTrigger value="staff" className="flex items-center space-x-2">
          <Users className="h-4 w-4" />
          <span className="hidden sm:inline">{t("notifications.tabs.staff")}</span>
        </TabsTrigger>
        <TabsTrigger value="inventory" className="flex items-center space-x-2">
          <Package className="h-4 w-4" />
          <span className="hidden sm:inline">{t("notifications.tabs.inventory")}</span>
        </TabsTrigger>
        <TabsTrigger value="reservations" className="flex items-center space-x-2">
          <Calendar className="h-4 w-4" />
          <span className="hidden sm:inline">{t("notifications.tabs.reservations")}</span>
        </TabsTrigger>
        <TabsTrigger value="payments" className="flex items-center space-x-2">
          <CreditCard className="h-4 w-4" />
          <span className="hidden sm:inline">{t("notifications.tabs.payments")}</span>
        </TabsTrigger>
        <TabsTrigger value="system" className="flex items-center space-x-2">
          <Settings className="h-4 w-4" />
          <span className="hidden sm:inline">{t("notifications.tabs.system")}</span>
        </TabsTrigger>
      </TabsList>

      <TabsContent value={activeTab} className="mt-6">
        {children}
      </TabsContent>
    </Tabs>
  );
}; 